import React, { useCallback, useState } from 'react';
import { Dialog, DialogBody, DialogHeader, Icon, } from '@repo/ui';
import { Appeal, MatterInvoiceSummaryFile } from '../../types';
import { NoAppealForm } from '../common/no-appeal';
import {  useMatterInvoiceSummaryFilesQuery } from '../../queries';


interface AppealFileUploadProps {
  invoiceId: string;
  isOpen: boolean;
  isLoading?: boolean;
  basicInformation?: Partial<Appeal>;
  onClose: () => void;
  onSubmit: (files: File[], comment: string) => Promise<void>;
}

/**
 * Modal wrapper around the NoAppealForm component.
 * Collects supporting files for a no-appeal write-off workflow.
 */
export const MarkNoAppealModal: React.FC<AppealFileUploadProps> = ({
  invoiceId,
  isOpen,
  isLoading = false,
  basicInformation,
  onClose,
  onSubmit,
}) => {

  const {
    data: existingFilesResponse,
    isLoading: isLoadingExistingFiles,
  } = useMatterInvoiceSummaryFilesQuery(invoiceId, { enabled: isOpen });
  const [files, setFiles] = useState<File[]>([]);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<{ comment: string; files: string }>({
    comment: '',
    files: '',
  });
  const existingFiles = existingFilesResponse?.data || [];

  const [selectedExistingFiles, setSelectedExistingFiles] = useState<MatterInvoiceSummaryFile[]>(
    []
  );
 

  const handleFilesChange = useCallback(
    (nextFiles: File[]) => {
      setFiles(nextFiles);
      // Clear error if either new files or existing files are selected
      const hasFiles = nextFiles.length > 0 || selectedExistingFiles.length > 0;
      setErrors(prev => ({
        ...prev,
        files: hasFiles ? '' : prev.files,
      }));
    },
    [selectedExistingFiles.length]
  );

  const handleExistingFilesChange = useCallback((files: MatterInvoiceSummaryFile[]) => {
    setSelectedExistingFiles(files);
    // Clear error if either new files or existing files are selected
    const hasFiles = files.length > 0;
    setErrors(prev => ({
      ...prev,
      files: hasFiles ? '' : prev.files,
    }));
  }, []);

  const handleClose = useCallback(() => {
    setFiles([]);
    setComment('');
    setErrors({
      comment: '',
      files: '',
    });
    setSelectedExistingFiles([]);
    onClose();
  }, [onClose]);

  const handleInputChange = useCallback((field: 'comment', value: string) => {
    if (field === 'comment') {
      setComment(value);
      setErrors(prev => ({ ...prev, comment: '' }));
    }
  }, []);

  const handleSetError = useCallback((field: 'comment' | 'files', error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  }, []);

  const handleSubmit = useCallback(async () => {
    await onSubmit(files, comment);
    setFiles([]);
    setComment('');
  }, [comment, files, onSubmit]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="xxllg"
      showCloseButton={false}
      className="!max-h-[90vh] overflow-y-auto"
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            No Appeal - INV #{basicInformation?.invoiceNumber ?? ''}
          </h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">
       
          <NoAppealForm
            invoiceId={invoiceId}
            isOpen={isOpen}
            matterDisplayNumber={basicInformation?.matterDisplayNumber ?? ''}
            clientName={basicInformation?.clientName ?? ''}
            invoiceDate={basicInformation?.invoiceDate ?? ''}
            invoiceNumber={basicInformation?.invoiceNumber ?? ''}
            outstandingAmount={basicInformation?.amountDue ?? 0}
            amountPaid={basicInformation?.paid ?? 0}
            totalAmount={basicInformation?.totalAmount ?? 0}
            creditsIssued={basicInformation?.creditsIssued ?? 0}
            responsibleAttorneyName={basicInformation?.responsibleAttorneyName ?? ''}
            isLoadingExistingFiles={isLoadingExistingFiles}
            comment={comment}
            files={files}
            existingFiles={existingFiles}
            errors={errors}
            isLoading={isLoading}
            onInputChange={handleInputChange}
            onFilesChange={handleFilesChange}
            onExistingFilesChange={handleExistingFilesChange}
            onSubmit={handleSubmit}
            setError={handleSetError}
            selectedExistingFiles={selectedExistingFiles}
          />
      </DialogBody>
    </Dialog>
  );
};



export default MarkNoAppealModal;
